import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export type SpecialReason =
  | 'uppercase'
  | 'uri-or-deeplink'
  | 'identifier'
  | 'special-characters'
  | 'key-column'
  | 'patch-history';

export type RenameContentDbSource = 'mongo' | 'mysql';

export interface RenameContentScope {
  dbSource?: RenameContentDbSource | null;
  targets?: string[];
}

export interface RenameContentChange {
  key?: string;
  source: 'mongo' | 'mysql';
  collectionOrTable: string;
  id: string | number | null;
  fieldOrColumn: string;
  oldValue: string;
  newValue: string;
  mysqlField?: string | null;
  isSpecialCase?: boolean;
  specialReasons?: SpecialReason[];
  matchedSubstrings?: string[];
  /** Present on apply reports — true if this row was written. */
  applied?: boolean;
}

export interface RenameContentRunReport {
  success: boolean;
  from?: string;
  to?: string;
  mode?: 'dry-run' | 'apply' | 'rollback';
  environment?: string;
  planId?: string;
  /** Present after a successful apply with writes — used for rollback (up to 1 month). */
  applyId?: string;
  appliedAt?: string;
  scope?: RenameContentScope;
  changeCount?: number;
  safeChangeCount?: number;
  specialChangeCount?: number;
  discardedCount?: number;
  appliedCount?: number;
  appliedSpecialCount?: number;
  writesPerformed?: boolean;
  changes?: RenameContentChange[];
  durationMs?: number;
  errorMessage?: string;
  note?: string;
  triggeredBy?: { name?: string; email?: string };
}

export interface ScanMatch {
  path?: string;
  column?: string;
  value: string;
  isSpecialCase?: boolean;
  specialReasons?: SpecialReason[];
  matchedSubstrings?: string[];
}

export interface RenameContentScanReport {
  success?: boolean;
  searchTerm?: string;
  environment?: string;
  scope?: RenameContentScope;
  durationMs?: number;
  errorMessage?: string;
  note?: string;
  mongo?: {
    collectionsScanned: number;
    actionable: Array<{
      collection: string;
      id: number | string | null;
      _id: string | null;
      matches: ScanMatch[];
    }>;
    omittedAuditOrOutOfScope: Array<{ collection: string; matchCount: number }>;
  };
  mysql?: {
    tablesScanned: number;
    actionable: Array<{
      table: string;
      recordId: number | string | null;
      field: string | null;
      matchedColumns: ScanMatch[];
    }>;
    keyColumnMatches: Array<{
      table: string;
      recordId: number | string | null;
      field: string | null;
      value: string;
      isSpecialCase?: boolean;
      specialReasons?: SpecialReason[];
    }>;
    omittedAuditOrOutOfScope: Array<{ table: string; rowCount: number }>;
  };
}

export interface RenameContentTargetsResponse {
  success: boolean;
  source?: RenameContentDbSource;
  names: string[];
  errorMessage?: string;
}

export interface RenameContentLatestApplyResponse {
  success: boolean;
  available: boolean;
  environment?: string;
  applyId?: string;
  planId?: string;
  from?: string;
  to?: string;
  appliedAt?: string;
  scope?: RenameContentScope;
  changeCount?: number;
  appliedCount?: number;
  safeChangeCount?: number;
  specialChangeCount?: number;
  changes?: RenameContentChange[];
  triggeredBy?: { name?: string; email?: string };
  note?: string;
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RenameContentApiService {
  private readonly apiUrlPrefix = '/api/rename-content';

  constructor(private http: HttpClient) {}

  dryRun(from: string, to: string, scope?: RenameContentScope): Promise<RenameContentRunReport> {
    return firstValueFrom(
      this.http.post<RenameContentRunReport>(`${this.apiUrlPrefix}/dry-run`, {
        from,
        to,
        ...this.scopeBody(scope),
      }),
    );
  }

  apply(
    from: string,
    to: string,
    planId: string,
    discardedKeys: string[],
    scope?: RenameContentScope,
  ): Promise<RenameContentRunReport> {
    return firstValueFrom(
      this.http.post<RenameContentRunReport>(`${this.apiUrlPrefix}/apply`, {
        from,
        to,
        planId,
        discardedKeys,
        ...this.scopeBody(scope),
      }),
    );
  }

  scan(term: string, scope?: RenameContentScope): Promise<RenameContentScanReport> {
    let params = new HttpParams().set('term', term);
    if (scope?.dbSource) {
      params = params.set('dbSource', scope.dbSource);
    }
    if (scope?.targets?.length) {
      params = params.set('targets', scope.targets.join(','));
    }
    return firstValueFrom(
      this.http.get<RenameContentScanReport>(`${this.apiUrlPrefix}/scan`, { params }),
    );
  }

  listTargets(source: RenameContentDbSource): Promise<RenameContentTargetsResponse> {
    return firstValueFrom(
      this.http.get<RenameContentTargetsResponse>(`${this.apiUrlPrefix}/targets`, {
        params: { source },
      }),
    );
  }

  getLatestApply(): Promise<RenameContentLatestApplyResponse> {
    return firstValueFrom(
      this.http.get<RenameContentLatestApplyResponse>(`${this.apiUrlPrefix}/latest-apply`),
    );
  }

  rollback(applyId: string): Promise<RenameContentRunReport> {
    return firstValueFrom(
      this.http.post<RenameContentRunReport>(`${this.apiUrlPrefix}/rollback`, { applyId }),
    );
  }

  private scopeBody(scope?: RenameContentScope): RenameContentScope {
    if (!scope?.dbSource) return {};
    return {
      dbSource: scope.dbSource,
      ...(scope.targets?.length ? { targets: scope.targets } : {}),
    };
  }
}
